import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import i18n from '@/utils/i18n'
import { USE_MOCK, YANDEX_MAPS_API_KEY } from '@/config'
import {
  loadYandexMaps,
  resetYandexMapsLoader,
  yandexMapsLoadFailed,
} from '@/lib/yandexMapsLoader'
import { useOrdersForMap, type OrderMapRow } from '@/hooks/useOrdersForMap'
import { statusLabelRu } from '@/utils/status-transitions'
import { formatRub } from '@/utils/format'
import type { OrderStatus } from '@/types/enums'
import { MAP_ACTIVE_STATUSES } from '@/features/map/orderMapStatusColors'
import { mapPinDataUrl } from '@/features/map/mapPinIcon'
import {
  mapOrderStatusMarkerFillHex,
  mapStatusBalloonBadgeStyle,
} from '@/features/map/mapStatusPalette'

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    ymaps: any
  }
}

/** API 2.1: центр [широта, долгота] */
const MAP_VIEW_STORAGE_KEY = 'map-view-state-21'
const MOSCOW_CENTER: [number, number] = [55.751244, 37.618423]
const MOSCOW_ZOOM = 10

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function balloonHtml(order: {
  id: string
  brand: string
  model: string
  desired_price: number | null
  final_price: number | null
  address: string
  status: OrderStatus
}): string {
  const title = escapeHtml(`${order.brand} ${order.model}`.trim())
  const status = escapeHtml(statusLabelRu(order.status))
  const amount = order.final_price ?? order.desired_price
  const price = formatRub(amount)
  const fill = mapOrderStatusMarkerFillHex(order.status)
  const badge = mapStatusBalloonBadgeStyle(fill)
  const statusBg = escapeHtml(badge.background)
  const statusColor = escapeHtml(badge.color)
  const addr = escapeHtml(order.address)
  const priceLabel = escapeHtml(i18n.t('geo.mapPage.balloonPrice'))
  const addressLabel = escapeHtml(i18n.t('geo.mapPage.balloonAddress'))
  const openLabel = escapeHtml(i18n.t('geo.mapPage.balloonOpenOrder'))
  return `<div style="font-family:Inter,system-ui,sans-serif;font-size:13px;max-width:240px;padding:4px 0;line-height:1.35">
  <div style="font-weight:600;margin-bottom:6px">${title}</div>
  <div style="display:inline-block;padding:2px 8px;border-radius:6px;background:${statusBg};color:${statusColor};font-size:11px;margin-bottom:8px">${status}</div>
  <div style="margin-bottom:4px"><span style="opacity:0.75">${priceLabel}</span> ${escapeHtml(price)}</div>
  <div style="margin-bottom:10px;opacity:0.9"><span style="opacity:0.75">${addressLabel}</span> ${addr}</div>
  <a href="/orders/${escapeHtml(order.id)}" style="display:inline-block;padding:8px 12px;background:#f7931a;color:#0d0f14;border-radius:8px;text-decoration:none;font-weight:600">${openLabel}</a>
</div>`
}

function readStoredView(): { center: [number, number]; zoom: number } | null {
  try {
    const raw = sessionStorage.getItem(MAP_VIEW_STORAGE_KEY)
    if (!raw) return null
    const j = JSON.parse(raw) as { center?: [number, number]; zoom?: number }
    if (
      !j.center ||
      typeof j.zoom !== 'number' ||
      j.center.length !== 2
    ) {
      return null
    }
    return { center: j.center, zoom: j.zoom }
  } catch {
    return null
  }
}

export function MapPage() {
  const { t } = useTranslation()
  const { markers, n, m, isLoading } = useOrdersForMap()
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const placemarksRef = useRef<any[]>([])
  const initialFitDoneRef = useRef(false)
  const [mapReady, setMapReady] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<OrderMapRow | null>(null)
  const [legendOpen, setLegendOpen] = useState(
    typeof window !== 'undefined' ? window.innerWidth > 768 : true,
  )
  const [mapLoadError, setMapLoadError] = useState<string | null>(null)
  const [mapInitTick, setMapInitTick] = useState(0)

  useLayoutEffect(() => {
    if (!YANDEX_MAPS_API_KEY) return

    const el = containerRef.current
    if (!el) return

    let cancelled = false
    let createdMap: any = null
    let bootstrapInProgress = false

    const destroyMap = () => {
      if (createdMap) {
        try {
          createdMap.destroy()
        } catch {
          /* empty */
        }
        createdMap = null
      }
      mapRef.current = null
      setMapReady(false)
      placemarksRef.current = []
      initialFitDoneRef.current = false
    }

    const bootstrap = async () => {
      if (cancelled || mapRef.current) return
      if (bootstrapInProgress) return
      const r = el.getBoundingClientRect()
      if (r.width < 4 || r.height < 4) return
      bootstrapInProgress = true

      try {
        try {
          await loadYandexMaps()
        } catch (e) {
          if (!cancelled) {
            setMapLoadError(
              e instanceof Error ? e.message : yandexMapsLoadFailed(),
            )
          }
          return
        }

        const ymaps = window.ymaps
        if (!ymaps) {
          if (!cancelled) {
            setMapLoadError(yandexMapsLoadFailed())
          }
          return
        }

        if (cancelled) return

        if (!cancelled) {
          setMapLoadError(null)
        }

        const stored = readStoredView()
        const center = stored?.center ?? MOSCOW_CENTER
        const zoom = stored?.zoom ?? MOSCOW_ZOOM

        const map = new ymaps.Map(
          el,
          {
            center,
            zoom,
            controls: ['zoomControl', 'fullscreenControl'],
          },
          {
            suppressMapOpenBlock: true,
          },
        )

        map.events.add('boundschange', () => {
          try {
            const c = map.getCenter()
            const z = map.getZoom()
            sessionStorage.setItem(
              MAP_VIEW_STORAGE_KEY,
              JSON.stringify({ center: c, zoom: z }),
            )
          } catch {
            /* empty */
          }
        })

        if (cancelled) {
          try {
            map.destroy()
          } catch {
            /* empty */
          }
          return
        }

        createdMap = map
        mapRef.current = map
        setMapReady(true)
      } finally {
        bootstrapInProgress = false
      }
    }

    const ro = new ResizeObserver(() => {
      const map = mapRef.current
      if (map?.container?.fitToViewport) {
        try {
          map.container.fitToViewport()
        } catch {
          /* empty */
        }
      }
      void bootstrap()
    })
    ro.observe(el)
    void bootstrap()

    return () => {
      cancelled = true
      ro.disconnect()
      destroyMap()
    }
  }, [mapInitTick])

  useEffect(() => {
    const map = mapRef.current
    const ymaps = window.ymaps
    if (!mapReady || !map || !ymaps) return

    for (const pm of placemarksRef.current) {
      try {
        map.geoObjects.remove(pm)
      } catch {
        /* empty */
      }
    }
    placemarksRef.current = []

    for (const o of markers) {
      const color = mapOrderStatusMarkerFillHex(o.status)
      const href = mapPinDataUrl(color)
      const placemark = new ymaps.Placemark(
        [o.lat, o.lon],
        {},
        {
          iconLayout: 'default#image',
          iconImageHref: href,
          iconImageSize: [32, 40],
          iconImageOffset: [-16, -40],
        },
      )
      placemark.events.add('click', () => {
        setSelectedOrder(o)
      })
      map.geoObjects.add(placemark)
      placemarksRef.current.push(placemark)
    }

    const stored = readStoredView()

    if (markers.length > 0) {
      if (!initialFitDoneRef.current && !stored) {
        try {
          if (markers.length === 1) {
            const o = markers[0]
            map.setCenter([o.lat, o.lon], 12, { duration: 0 })
          } else {
            const points = markers.map((x) => [x.lat, x.lon] as [number, number])
            const bounds = ymaps.util.bounds.fromPoints(points)
            map.setBounds(bounds, {
              checkZoomRange: true,
              zoomMargin: 48,
              duration: 0,
            })
          }
        } catch {
          map.setCenter(MOSCOW_CENTER, MOSCOW_ZOOM, { duration: 0 })
        }
        initialFitDoneRef.current = true
      }
    } else if (!stored) {
      try {
        map.setCenter(MOSCOW_CENTER, MOSCOW_ZOOM, { duration: 0 })
      } catch {
        /* empty */
      }
    }
  }, [markers, mapReady])

  if (!YANDEX_MAPS_API_KEY) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center rounded-xl border border-dashed border-[var(--stryzh-color-border-default)] bg-[var(--stryzh-color-surface)] p-8 text-center">
        <p className="text-[color:var(--stryzh-color-text-primary)]">
          {t('geo.mapPage.noApiKeyTitle')}
        </p>
        <p className="mt-2 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('geo.mapPage.noApiKeyBody')}
        </p>
      </div>
    )
  }

  return (
    <div
      className={clsx(
        'relative -mx-4 -mb-4 flex min-h-0 flex-1 flex-col',
        'h-[calc(100dvh-var(--stryzh-layout-header-height)-2rem)] lg:h-[calc(100dvh-2rem)]',
      )}
    >
      {mapLoadError && !mapReady ? (
        <div
          className="pointer-events-none absolute left-3 right-3 top-14 z-20 mx-auto max-w-xl rounded-lg border border-[color:var(--stryzh-color-semantic-error)]/50 bg-[color:var(--stryzh-color-surface)] px-4 py-3 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)] shadow-lg"
          style={{ pointerEvents: 'auto' }}
          role="alert"
        >
          <p className="font-medium text-[color:var(--stryzh-color-semantic-error)]">
            {t('geo.mapPage.loadFailedTitle')}
          </p>
          <p className="mt-2 text-[color:var(--stryzh-color-text-secondary)]">{mapLoadError}</p>
          <button
            type="button"
            className="mt-3 rounded-lg bg-[color:var(--stryzh-color-border-subtle)] px-3 py-1.5 text-[length:var(--stryzh-font-size-xs)] font-medium text-[color:var(--stryzh-color-text-primary)] hover:bg-[color:var(--stryzh-color-border-default)]"
            onClick={() => {
              resetYandexMapsLoader()
              setMapLoadError(null)
              setMapInitTick((x) => x + 1)
            }}
          >
            {t('geo.mapPage.retryLoad')}
          </button>
        </div>
      ) : null}

      <div
        className="pointer-events-none absolute left-3 top-3 z-10 max-w-[min(92vw,360px)] rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)]/85 px-3 py-2 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)] shadow-md backdrop-blur-sm"
        style={{ pointerEvents: 'auto' }}
      >
        {isLoading ? (
          <span className="opacity-80">{t('geo.mapPage.loading')}</span>
        ) : (
          <>
            <span>
              {t('geo.mapPage.shownOfActive', { n, m })}
            </span>
            {n < m ? (
              <p className="mt-1 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                {USE_MOCK
                  ? t('geo.mapPage.missingCoordsMock', { count: m - n })
                  : t('geo.mapPage.missingCoordsReal', { count: m - n })}
              </p>
            ) : null}
          </>
        )}
      </div>

      {selectedOrder ? (
        <div
          className="pointer-events-none absolute left-1/2 top-[calc(3.5rem+env(safe-area-inset-top,0px))] z-20 w-[min(92vw,280px)] -translate-x-1/2"
          style={{ pointerEvents: 'auto' }}
        >
          <div className="relative rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)]/98 p-3 pr-9 shadow-lg backdrop-blur-sm">
            <button
              type="button"
              className="absolute right-2 top-2 rounded p-1 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
              onClick={() => setSelectedOrder(null)}
              aria-label={t('geo.close')}
            >
              ✕
            </button>
            <div
              dangerouslySetInnerHTML={{
                __html: balloonHtml(selectedOrder),
              }}
            />
          </div>
        </div>
      ) : null}

      <div className="relative w-full min-h-0 flex-1">
        <div
          ref={containerRef}
          className="map-container stryzh-map-canvas--dark absolute inset-0 overflow-hidden rounded-lg border border-[color:var(--stryzh-color-border-default)]"
        />
      </div>

      <MapLegend open={legendOpen} onToggle={() => setLegendOpen((v) => !v)} />
    </div>
  )
}

function MapLegend({
  open,
  onToggle,
}: {
  open: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  return (
    <div
      className="pointer-events-none absolute bottom-3 right-3 z-10 max-w-[min(92vw,280px)]"
      style={{ pointerEvents: 'auto' }}
    >
      <div className="rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)]/95 shadow-lg backdrop-blur-sm">
        <div className="flex items-center justify-between gap-2 border-b border-[color:var(--stryzh-color-border-subtle)] px-3 py-2">
          <span className="font-medium text-[color:var(--stryzh-color-text-primary)]">
            {t('geo.mapPage.statuses')}
          </span>
          <button
            type="button"
            className="rounded px-2 py-1 text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)] hover:bg-[color:var(--stryzh-color-border-subtle)]"
            onClick={onToggle}
            aria-expanded={open}
          >
            {open ? t('geo.mapPage.collapse') : t('geo.mapPage.expand')}
          </button>
        </div>
        {open ? (
          <ul className="max-h-[min(50vh,360px)] space-y-1.5 overflow-y-auto px-3 py-2">
            {Array.from(MAP_ACTIVE_STATUSES, (st) => {
              const fill = mapOrderStatusMarkerFillHex(st)
              return (
                <li key={st} className="flex items-center gap-2 text-[length:var(--stryzh-font-size-sm)]">
                  <span
                    className="h-3 w-3 shrink-0 rounded-full border border-[color:var(--stryzh-color-border-default)]"
                    style={{ background: fill }}
                  />
                  <span className="text-[color:var(--stryzh-color-text-primary)]">
                    {statusLabelRu(st)}
                  </span>
                </li>
              )
            })}
          </ul>
        ) : null}
      </div>
    </div>
  )
}
